import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Item type in copy — "video", "project", "asset". Kept singular. */
  itemLabel: string;
  /** Display name shown in bold in the title. Ignored when `itemCount > 1`. */
  itemName: string;
  /**
   * Bulk mode (2026-07-18). When >1 the title reads "Delete N assets?" and the
   * item name is dropped. Caller is responsible for fanning out the actual
   * per-item mutations inside onTrash/onPermanent — the dialog just fires the
   * handler once. Defaults to 1 (single-item behavior unchanged).
   */
  itemCount?: number;
  /** Trash TTL in days — displayed in the copy. Matches backend TRASH_TTL_MS. */
  trashDays?: number;
  /**
   * "both"      — active-item flow: shows Move to Trash + Delete Permanently.
   * "permanent" — trash-page flow: item is already trashed, only Delete Permanently visible.
   * Default "both".
   */
  mode?: "both" | "permanent";
  /**
   * Handler for the trash branch. Required unless mode="permanent".
   * Dialog closes on resolve. If it throws, error stays visible.
   */
  onTrash?: () => Promise<void>;
  /**
   * Handler for the permanent branch. Always required.
   * Dialog closes on resolve. If it throws, error stays visible.
   */
  onPermanent: () => Promise<void>;
};

/**
 * Google-Drive-style two-branch delete dialog.
 * Locked model 2026-07-08 (see memory: project-trash-delete-model).
 *
 *   - Move to Trash → 1-click, no type gate. Recoverable for {trashDays} days.
 *   - Delete Permanently → type "confirm" required. Irreversible.
 *
 * Rule: friction proportional to danger. Trash is safe, so no gate. Permanent
 * gate exists so users literally have to stop and type before nuking.
 */
export function DeleteDialog({
  open,
  onOpenChange,
  itemLabel,
  itemName,
  itemCount = 1,
  trashDays = 10,
  mode = "both",
  onTrash,
  onPermanent,
}: Props) {
  const isBulk = itemCount > 1;
  const showTrash = mode === "both";
  const [confirmText, setConfirmText] = useState("");
  const [busyBranch, setBusyBranch] = useState<"trash" | "permanent" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isPending = busyBranch !== null;
  const permanentEnabled = confirmText.trim().toLowerCase() === "confirm";

  function reset() {
    setConfirmText("");
    setBusyBranch(null);
    setErrorMessage(null);
  }

  async function runTrash() {
    if (!onTrash) return;
    setErrorMessage(null);
    setBusyBranch("trash");
    try {
      await onTrash();
      onOpenChange(false);
      reset();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to move to trash.");
      setBusyBranch(null);
    }
  }

  async function runPermanent() {
    setErrorMessage(null);
    setBusyBranch("permanent");
    try {
      await onPermanent();
      onOpenChange(false);
      reset();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to delete permanently.");
      setBusyBranch(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (isPending) return;
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isBulk ? (
              <>
                Delete <span className="font-semibold">{itemCount} {itemLabel}s</span>?
              </>
            ) : (
              <>
                Delete "<span className="font-semibold">{itemName}</span>"?
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Trash branch — 1-click execute. Hidden in permanent-only mode. */}
          {showTrash && (
            <div className="rounded-lg border border-border p-3">
              <div className="font-medium text-foreground">Move to Trash</div>
              <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
                <li>Recoverable for {trashDays} days.</li>
                <li>Still counts toward your storage quota.</li>
                <li>Auto-purged after {trashDays} days.</li>
              </ul>
              <Button
                type="button"
                variant="outline"
                className="mt-3 w-full"
                onClick={runTrash}
                disabled={isPending}
              >
                {busyBranch === "trash" ? "Moving to trash…" : "Move to Trash"}
              </Button>
            </div>
          )}

          {/* Permanent branch — gated by "confirm" typing. */}
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3">
            <div className="font-medium text-destructive">Delete Permanently</div>
            <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
              <li>
                <span className="font-medium text-foreground">Cannot be recovered.</span>
              </li>
              <li>Frees storage immediately.</li>
            </ul>
            <div className="mt-3 space-y-1.5">
              <Label htmlFor="delete-confirm" className="text-xs text-muted-foreground">
                Type <span className="font-semibold text-foreground">confirm</span> to enable
              </Label>
              <Input
                id="delete-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="confirm"
                disabled={isPending}
                autoComplete="off"
              />
            </div>
            <Button
              type="button"
              variant="destructive"
              className="mt-3 w-full"
              onClick={runPermanent}
              disabled={isPending || !permanentEnabled}
            >
              {busyBranch === "permanent"
                ? "Deleting…"
                : isBulk
                  ? `Delete ${itemCount} ${itemLabel}s permanently`
                  : `Delete ${itemLabel} permanently`}
            </Button>
          </div>

          {errorMessage && (
            <p className="text-sm text-destructive" role="alert">
              {errorMessage}
            </p>
          )}

          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (!isPending) {
                  onOpenChange(false);
                  reset();
                }
              }}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
